import {MutableRefObject, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import './handle-task-form.scss';
import {useTranslation} from 'react-i18next';
import {Controller, useForm} from 'react-hook-form';
import moment from 'moment';
import {Box, Button, Collapse, Radio, RadioGroup, Stack} from '@mui/material';
import {VscRefresh} from 'react-icons/vsc';
import {CareTaskComplementary} from '../../../backend/interfaces/care-task-complementary';
import {CareTaskNotDoneReason} from '../../../backend/enums/care-task-not-done-reason';
import {PutEntityCareTask} from '../../../backend/requests/put-entity-care-task';
import {RadioLabel} from '../../shared/forms/radio';
import {HandleCareTaskFeatureDataFields} from '../../shared/handle-care-task-feature-data';
import {
  BADL_SECTOR_ID,
  CLINICAL_SUPPORT_SECTOR_ID,
  DEFAULT_FORM_VALIDATIONS,
  IADL_SECTOR_ID,
  PERSONAL_DEVELOPMENT_SECTOR_ID,
} from '../../../utils/constants';
import {CareTaskData, SleepHealthData} from '../../../backend/interfaces/care-task-data';
import {DailyCareSelect} from '../forms/daily-care-select';
import {DailyCareInput} from '../forms/daily-care-input';
import {useSnackBar} from '../../../contexts/snack-bar/context';
import {CareTaskFeature} from '../../../backend/enums/care-task-feature';
import {EntityUserCareTasksRepository} from '../../../repositories/entity-user-care-tasks';
import {translations} from '../../../i18n/translations';
import {useAuth} from '../../../contexts/auth/context';
import {RolePermissionType} from '../../../backend/enums/role-permission-type';
import {Tooltip} from '../../tooltip/tooltip';
import {DailyCareLogFormFooterButtons} from './daily-care-log-form-footer-buttons';
import {DailyCareLogModal} from './daily-care-modal';
import {ViewAttachments} from '../attachments/view-attachments';
import {AddAttachmentsForm, SavingAttachment} from '../attachments/add-attachments-form';
import {requestAttachmentStorageUrl} from '../../../screens/private/daily-care-log/unplanned-tasks/unplannedtask.daily-care-log.screen';
import {StorageType} from '../../../backend/enums';
import {TaskLogs} from '../../history-logs/task-logs/_task-logs';

interface FormData {
  markedAsDone?: 'done' | 'not-done';
  taskNotDoneReason?: CareTaskNotDoneReason;
  data?: CareTaskData;
  notes?: string;
  files?: SavingAttachment[];
}

interface HandleTaskFormProps {
  task: CareTaskComplementary;
  onSubmit: ({
    markedAsDone,
    executionDatetime,
    notes,
    taskNotDoneReason,
  }: PutEntityCareTask) => Promise<void>;
  onCancel: () => void;
}

const HandleTaskForm = ({task, onCancel, onSubmit}: HandleTaskFormProps) => {
  const [editMode, setEditMode] = useState<boolean | undefined>(undefined);
  const [confirmAction, setConfirmAction] = useState<'EDIT' | 'CANCEL_EDIT' | 'RESET' | undefined>(
    undefined
  );
  const {t} = useTranslation();
  const snackBarContext = useSnackBar();
  const [saving, setSaving] = useState(false);
  const notesWrapper = useRef() as MutableRefObject<HTMLDivElement>;
  const {
    register,
    control,
    setValue,
    getValues,
    reset,
    watch,
    clearErrors,
    setFocus,
    setError,
    formState: {isValid, isDirty, errors},
  } = useForm<FormData>({
    ...DEFAULT_FORM_VALIDATIONS,
  });
  const [files] = watch(['files']);
  const {hasWritePermission} = useAuth();
  const canWrite = useMemo(() => {
    // !!! NOTE: This assumes sectors are the same for all entities, as specified in the utils/constants/hardcoded.ts file.
    const SECTOR_ID_TO_PERMISSION_TYPE_MAPPING: Record<string, RolePermissionType> = {
      [BADL_SECTOR_ID]: RolePermissionType.DailyCareLog_BADL,
      [IADL_SECTOR_ID]: RolePermissionType.DailyCareLog_IADL,
      [CLINICAL_SUPPORT_SECTOR_ID]: RolePermissionType.DailyCareLog_ClinicalSupport,
      [PERSONAL_DEVELOPMENT_SECTOR_ID]: RolePermissionType.DailyCareLog_PersonalDevelopment,
    };

    const permissionType = SECTOR_ID_TO_PERMISSION_TYPE_MAPPING[task.careTask.activitiesSectorId];
    return hasWritePermission(permissionType);
  }, [hasWritePermission, task.careTask.activitiesSectorId]);

  const setPreffilledFormData = useCallback(() => {
    const defaultValues: FormData = {
      taskNotDoneReason: task?.careTask.taskNotDoneReason,
      notes: task?.careTask.notes,
      data: task?.careTask.data,
    };

    if (task?.careTask?.markedAsDone !== undefined) {
      defaultValues.markedAsDone = task?.careTask?.markedAsDone === true ? 'done' : 'not-done';
    }

    // Prefill the dates
    if (task?.careTask.feature === CareTaskFeature.SleepHealthMeasurement) {
      const sleepHealthData = defaultValues?.data as SleepHealthData | undefined;

      defaultValues.data = {
        ...defaultValues.data,
        startTime: sleepHealthData?.startTime || moment().toISOString(),
        endTime: sleepHealthData?.endTime || moment().add(1, 'hour').toISOString(),
      } as CareTaskData & {startTime?: string; endTime?: string};
    }

    reset(defaultValues);
  }, [
    reset,
    task?.careTask.data,
    task?.careTask.feature,
    task?.careTask?.markedAsDone,
    task?.careTask.notes,
    task?.careTask.taskNotDoneReason,
  ]);

  const undoTask = async () => {
    if (!task.careTask.entityId || !task.careTask.userId || !task?.careTask.ref) return;

    return EntityUserCareTasksRepository.instance
      .ResetCareTask(task.careTask.entityId, task.careTask.userId, task.careTask.ref)
      .then(() => {
        snackBarContext.setDailyCareLogToastNotification({
          severity: 'success',
          message: t(translations.care_and_activities.handle_task_modal.snackbar.reset.success),
        });

        // Hide confirmation modal
        hideConfirmationModal();

        // Redirect
        onCancel();
      })
      .catch((error) => {
        snackBarContext.setDailyCareLogToastNotification({
          severity: 'error',
          message: t(translations.care_and_activities.handle_task_modal.snackbar.reset.error),
        });

        console.debug('🔴 Error handling the care task', error);
      });
  };

  const updateTask = async () => {
    const [markedAsDone, taskNotDoneReason, notes, data] = getValues([
      'markedAsDone',
      'taskNotDoneReason',
      'notes',
      'data',
    ]);

    const updateData: PutEntityCareTask = {
      executionDatetime: moment().toISOString(),
      markedAsDone: markedAsDone === 'done' ? true : false,
      notes: notes || null,
    };

    setSaving(true);

    if (files) {
      const result = await requestAttachmentStorageUrl(
        task.careTask.entityId,
        files,
        StorageType.CareTaskAttachment
      );
      if (result === null) {
        snackBarContext.setDailyCareLogToastNotification({
          severity: 'error',
          message: t(translations.daily_care_log.shift_details.unplanned_tasks.upload_error_toast),
        });
        setSaving(false);
        return;
      } else {
        updateData.attachments = result;
      }
    }

    if (updateData.markedAsDone) {
      if (data && task?.careTask.feature) {
        let cleanData: Record<any, any> = {};

        for (const d in data) {
          // @ts-ignore
          cleanData[d] = data[d];

          // Convert date to string
          // @ts-ignore
          if (moment.isMoment(data[d])) {
            // @ts-ignore
            cleanData[d] = data[d].toISOString();
          }
        }

        // On the sleep measurement, we want to calculate the distance in minutes between start and end date
        if (task?.careTask.feature === CareTaskFeature.SleepHealthMeasurement) {
          // @ts-ignore
          if (data?.startTime && data?.endTime) {
            var timeDistance = moment.duration(
              // @ts-ignore
              moment(data?.endTime)
                .set('milliseconds', 0)
                // @ts-ignore
                .diff(moment(data?.startTime).set('milliseconds', 0))
            );

            cleanData = {
              ...cleanData,
              value: timeDistance.asMinutes(),
            };
          }
        }

        // @ts-ignore
        updateData.data = cleanData;
      }
    } else {
      updateData.taskNotDoneReason = taskNotDoneReason;
    }

    try {
      await onSubmit(updateData);
    } finally {
      setSaving(false);
    }
  };

  const fieldReadEditStyles = useMemo(() => {
    if (editMode && canWrite) {
      return {readOnly: false, disabled: false};
    } else {
      return {readOnly: true, disabled: true};
    }
  }, [editMode, canWrite]);

  // Methods for user actions

  const displayCancelEditConfirmationModal = () => setConfirmAction('CANCEL_EDIT');

  const hideConfirmationModal = () => setConfirmAction(undefined);

  // Observe fields to run side-effects
  const [isMarkedAsDone, notDoneReason, notesValue] = watch([
    'markedAsDone',
    'taskNotDoneReason',
    'notes',
  ]);

  const displayNotDoneReason = useMemo(() => isMarkedAsDone === 'not-done', [isMarkedAsDone]);

  const displayFeatureFields = useMemo(
    () => Boolean(isMarkedAsDone === 'done' && task.careTask.feature),
    [isMarkedAsDone, task.careTask.feature]
  );

  const notesAsMandatory = useMemo(
    () => Boolean(isMarkedAsDone === 'not-done' && notDoneReason === 'other'),
    [isMarkedAsDone, notDoneReason]
  );

  // Pre-fill data on mount
  useEffect(() => {
    setPreffilledFormData();
  }, [setPreffilledFormData]);

  // Observe for task changes and apply edit mode accordingly to the flag of markedAsHandled
  useEffect(() => {
    setEditMode(Boolean(!task?.careTask?.markedAsHandled));
  }, [task]);

  // Notes error explicit behaviour
  useEffect(() => {
    if (!notesAsMandatory && errors.notes?.message) {
      clearErrors('notes');
    } else if (notesAsMandatory && !errors.notes?.message && notesValue?.length === 0) {
      setError(
        'notes',
        {
          message: t(
            translations.care_and_activities.handle_task_modal.observation_required_message
          ),
        },
        {shouldFocus: true}
      );
      notesWrapper.current.scrollIntoView({behavior: 'smooth', block: 'start'});
    }
  }, [
    clearErrors,
    errors.notes?.message,
    notesAsMandatory,
    notesValue?.length,
    setError,
    setFocus,
    t,
  ]);

  return (
    <Stack className="handle-task-form" component="form">
      {/**
       * Mark as done
       */}
      <Controller
        name="markedAsDone"
        control={control}
        rules={{
          required: true,
        }}
        render={({field}) => (
          <RadioGroup row {...field}>
            <RadioLabel.Tooltip
              disabledTooltip={t(translations.daily_care_log.task_details.disabled_tooltip)}
              value="done"
              control={<Radio />}
              label={t(translations.daily_care_log.task_details.task_done)}
              checked={field.value === 'done'}
              {...fieldReadEditStyles}
            />
            <RadioLabel.Tooltip
              disabledTooltip={t(translations.daily_care_log.task_details.disabled_tooltip)}
              value="not-done"
              control={<Radio />}
              label={t(translations.daily_care_log.task_details.task_not_done)}
              checked={field.value === 'not-done'}
              {...fieldReadEditStyles}
            />
          </RadioGroup>
        )}
      />
      {/**
       * Not Done - reason
       */}
      <Collapse in={displayNotDoneReason} unmountOnExit>
        <Controller
          name="taskNotDoneReason"
          control={control}
          rules={{
            required: getValues('markedAsDone') === 'not-done',
          }}
          render={({field}) => (
            <DailyCareSelect
              {...field}
              label={t(translations.daily_care_log.task_details.task_not_done_reason)}
              options={Object.values(CareTaskNotDoneReason).map((option) => {
                return {
                  key: option,
                  label: t(translations.care_taks_not_done_reasons[option]),
                };
              })}
              displayEmpty
              {...fieldReadEditStyles}
            />
          )}
        />
      </Collapse>
      {/**
       * Data feature fields
       */}
      {task?.careTask?.feature && (
        <Collapse in={displayFeatureFields} unmountOnExit>
          <HandleCareTaskFeatureDataFields
            layout="daily-care-log"
            feature={task.careTask.feature}
            control={control}
            readOnly={!editMode}
          />
        </Collapse>
      )}
      {/**
       * Notes
       */}
      <Box ref={notesWrapper}>
        <Tooltip.NotAllowedIf
          condition={fieldReadEditStyles.disabled}
          title={t(translations.daily_care_log.task_details.disabled_tooltip)}
        >
          <DailyCareInput
            {...register('notes', {
              required: {
                value: notesAsMandatory,
                message: t(
                  translations.care_and_activities.handle_task_modal.observation_required_message
                ),
              },
            })}
            label={t(translations.daily_care_log.task_details.add_a_note)}
            fullWidth
            multiline
            maxRows={4}
            required={notesAsMandatory}
            errors={errors.notes?.message}
            sx={{paddingX: 1.5, paddingY: 2}}
            {...fieldReadEditStyles}
          />
        </Tooltip.NotAllowedIf>
      </Box>
      {/**
       * Task Attachments
       */}
      {task?.careTask.attachments && task?.careTask.attachments.length > 0 && !editMode && (
        <ViewAttachments isDailyCareLogApp attachments={task.careTask.attachments} />
      )}
      {editMode && (
        <Controller
          name="files"
          control={control}
          render={() => {
            return (
              <AddAttachmentsForm
                isDailyCareLogApp
                type="view"
                attachmentsUrls={task.careTask.attachments}
                onAttachmentsChange={(value) => setValue('files', value)}
                onError={(errors) => {
                  if (errors.length > 0) {
                    setError('files', {message: 'Error'});
                  } else {
                    clearErrors('files');
                  }
                }}
              />
            );
          }}
        />
      )}
      {/**
       * Task History
       */}
      {((task.careTask.changesHistory && task.careTask.changesHistory.length > 0) ||
        (task.careTask.history && task.careTask.history.length > 0)) && (
        <TaskLogs
          history={task?.careTask.changesHistory}
          legacyHistory={task?.careTask.history}
          feature={task.careTask.feature}
          type="compact"
        />
      )}
      {/**
       * Hability to reset a task
       */}
      {canWrite && editMode && task?.careTask?.markedAsHandled && (
        <Stack
          sx={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Button
            color="error"
            variant="text"
            startIcon={<VscRefresh />}
            sx={{maxWidth: '252px', width: '100%'}}
            onClick={() => setConfirmAction('RESET')}
          >
            {t(translations.care_and_activities.handle_task_modal.undo_task)}
          </Button>
        </Stack>
      )}
      <DailyCareLogFormFooterButtons
        canWrite={canWrite}
        editMode={!!editMode}
        isCancelDisabled={!isDirty && task?.careTask?.markedAsHandled === true}
        isSaveDisabled={!isValid || saving}
        isSaveLoading={saving}
        onSave={
          task?.careTask?.markedAsHandled === true ? () => setConfirmAction('EDIT') : updateTask
        }
        onCancel={
          canWrite
            ? isDirty
              ? displayCancelEditConfirmationModal
              : () => {
                  if (task?.careTask?.markedAsHandled) {
                    setEditMode(false);
                  } else {
                    onCancel();
                  }
                }
            : isDirty
              ? displayCancelEditConfirmationModal
              : onCancel
        }
        onEdit={() => setEditMode(true)}
      />
      {/**
       * Confirm reset task
       */}
      <DailyCareLogModal
        open={confirmAction === 'RESET'}
        title={t(translations.shared.handle_care_task_modal.confirm_reset.title)}
        subtitle={t(translations.shared.handle_care_task_modal.confirm_reset.subtitle)}
        onClose={hideConfirmationModal}
        onConfirm={undoTask}
      />
      {/**
       * Confirm task edit
       */}
      <DailyCareLogModal
        open={confirmAction === 'EDIT'}
        title={t(translations.shared.handle_care_task_modal.confirm_edit.title)}
        onClose={hideConfirmationModal}
        onConfirm={updateTask}
      />
      {/**
       * Confirm task edit cancel
       */}
      <DailyCareLogModal
        open={confirmAction === 'CANCEL_EDIT'}
        title={t(translations.shared.handle_care_task_modal.confirm_edit_cancel.title)}
        subtitle={t(translations.shared.handle_care_task_modal.confirm_edit_cancel.subtitle)}
        onClose={hideConfirmationModal}
        onConfirm={async () => onCancel()}
      />
    </Stack>
  );
};

export default HandleTaskForm;
